import { useState } from "react";
import { siteMeta } from "@/mocks/gossipData";

const FORM_URL = "https://readdy.ai/api/form/daf99ijep053ijub1ljg";

export default function Subscribe() {
  const [status, setStatus] = useState<"idle" | "sending" | "success">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError("");
    const form = e.currentTarget;
    const formData = new FormData(form);

    // anti-spam honeypot check
    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    setStatus("sending");
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "email") {
          body.append("email", String(value).trim());
        } else if (value) {
          body.append(key, String(value));
        }
      });

      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (
        res.ok &&
        (parsed?.code === "OK" || parsed?.code === undefined) &&
        !serverMsg.toLowerCase().includes("spam")
      ) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "订阅未成功，请稍后再试。");
        setStatus("idle");
      }
    } catch {
      setFormError("网络异常，订阅未成功，请稍后再试。");
      setStatus("idle");
    }
  }

  return (
    <section id="subscribe" className="relative overflow-hidden bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-foreground-950 px-6 py-12 md:px-14 md:py-16">
          <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-primary-500/30 blur-3xl"></div>
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-accent-500/25 blur-3xl"></div>

          <div className="relative z-10 grid gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-black text-background-50 md:text-3xl">
                订阅每周瓜报，不落任何一瓜
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-background-200">
                每周盘点发布后，我们会把本期焦点、近期动态与深度专题的完整入口发到你的邮箱。
                订阅即表示你同意接收我们每周一次的内容推送，可随时退订。
              </p>
              <ul className="mt-6 space-y-2 text-sm text-background-100">
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-accent-400"></i>每周一次，绝不打扰
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-accent-400"></i>覆盖四大栏目焦点
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-accent-400"></i>独家深度专题抢先看
                </li>
              </ul>
            </div>

            <div className="flex items-center">
              {status === "success" ? (
                <div className="w-full rounded-xl bg-background-50/10 px-6 py-8 text-center ring-1 ring-background-100/20">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-accent-950">
                    <i className="ri-check-line text-2xl"></i>
                  </span>
                  <p className="mt-4 font-heading text-lg font-bold text-background-50">订阅成功！</p>
                  <p className="mt-1 text-sm text-background-200">
                    欢迎加入瓜友行列，本周盘点将在发布时间第一时间送达。
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-4 whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-background-200"
                  >
                    再订阅一个邮箱
                  </button>
                </div>
              ) : (
                <form
                  id="newsletter-form"
                  data-readdy-form="吃瓜热门订阅"
                  onSubmit={handleSubmit}
                  className="w-full rounded-xl bg-background-50 p-6 ring-1 ring-background-100/20 md:p-7"
                >
                  <label htmlFor="subscribe-email" className="text-sm font-semibold text-foreground-900">
                    你的邮箱地址
                  </label>
                  <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                    <input
                      id="subscribe-email"
                      name="email"
                      type="email"
                      required
                      maxLength={120}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="h-11 flex-1 rounded-md bg-background-100 px-3 text-sm text-foreground-900 outline-none ring-1 ring-background-300 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
                    />
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                    >
                      {status === "sending" ? "提交中…" : "立即订阅"}
                    </button>
                  </div>
                  <input
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp"
                    placeholder="website_alt"
                  />
                  <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                    我们只会在订阅后发送本周焦点推送，你的邮箱不会被用于其它用途。
                  </p>
                  {formError && (
                    <p className="mt-3 flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-2 text-xs font-medium text-primary-700">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-xl bg-background-100 p-5 ring-1 ring-background-200 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-semibold text-foreground-900">想聊聊？欢迎给我们来信</p>
            <p className="mt-1 text-xs text-foreground-500">选题建议、纠错反馈或合作，都可以写给我们。</p>
          </div>
          <a
            href={`mailto:${siteMeta.contactEmail}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-200 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700"
          >
            <i className="ri-mail-line"></i>
            {siteMeta.contactEmail}
          </a>
        </div>
      </div>
    </section>
  );
}